import { AuthStatus } from '@/enums';
import { FocusSource, useComponentFocus } from './use-component-focus';
import { gql, useMutation } from '@apollo/client';
import { SetupAppMutation, SetupAppMutationVariables } from '@/types';
import { useCallback, useEffect } from 'react';
import { useMaybeUserContext } from '@/contexts/user-context';
import { useThrottledCallback } from 'use-debounce';

export const SETUP_APP_MUTATION = gql`
  mutation SetupApp($restaurantUUID: uuid!, $supplierUUID: uuid!) {
    setupApp(restaurant_id: $restaurantUUID, supplier_id: $supplierUUID) {
      restaurant_id
    }
  }
`;

export const THROTTLE_MS = 60 * 1000; // 1 minute

export const useSetupApp = () => {
  const userContext = useMaybeUserContext();

  const [setupApp] = useMutation<SetupAppMutation, SetupAppMutationVariables>(
    SETUP_APP_MUTATION,
    {
      context: {
        noBatch: true,
      },
    },
  );

  const restaurantUUID =
    userContext?.authStatus === AuthStatus.Authenticated
      ? userContext?.selectedRestaurantRelationship?.uuid
      : null;
  const supplierUUID =
    userContext?.authStatus === AuthStatus.Authenticated
      ? userContext?.selectedRestaurantRelationship?.supplierUUID
      : null;

  const maybeSetupApp = useThrottledCallback(
    async () => {
      if (!restaurantUUID || !supplierUUID) {
        return;
      }

      try {
        await setupApp({
          variables: {
            restaurantUUID,
            supplierUUID,
          },
        });
      } catch (e) {
        // Do nothing
      }
    },
    THROTTLE_MS,
    { trailing: false },
  );

  useComponentFocus({
    onFocus: useCallback(
      (focusSource: FocusSource) => {
        if (focusSource === FocusSource.App) {
          maybeSetupApp();
        }
      },
      [maybeSetupApp],
    ),
  });

  useEffect(() => {
    // Cancel any throttled calls so it's called immediately when the restaurant or supplier changes
    maybeSetupApp.cancel();
    maybeSetupApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantUUID, supplierUUID]);
};
